/**
 * Reports Data Hook
 * Custom hook for managing report data fetching and state
 */

import { useState, useCallback } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import ReportsDataService from '../services/ReportsDataService';

export const useReportsData = () => {
  const { token, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [fromCache, setFromCache] = useState(false);

  const generateReport = useCallback(async (reportType, parameters = {}) => {
    if (!token || authLoading) {
      setError('Authentication required');
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      // Validate parameters before making request
      ReportsDataService.validateParameters(reportType, parameters);

      const result = await ReportsDataService.generateReport(reportType, parameters, token);
      
      setReportData(result);
      setFromCache(result.fromCache || false);
      
      return result;
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [token, authLoading]);

  const batchGenerate = useCallback(async (reportRequests) => {
    if (!token || authLoading) {
      setError('Authentication required');
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      // Validate all requests
      reportRequests.forEach(request => {
        ReportsDataService.validateParameters(request.type, request.parameters);
      });

      const results = await ReportsDataService.batchGenerate(reportRequests, token);
      
      return results;
    } catch (err) {
      console.error('Error in batch report generation:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [token, authLoading]);

  const clearCache = useCallback((reportType = null) => {
    ReportsDataService.clearCache(reportType);
  }, []);

  const getCacheStats = useCallback(() => {
    return ReportsDataService.getCacheStats();
  }, []);

  return {
    loading,
    error,
    reportData,
    fromCache,
    generateReport,
    batchGenerate,
    clearCache,
    getCacheStats,
    // Utility methods
    setError,
    setLoading,
    setReportData
  };
};

// Specialized hooks for different report types
export const useDailyLogData = () => {
  const { generateReport, loading, error, reportData, fromCache } = useReportsData();

  const fetchDailyLog = useCallback(async (date) => {
    return await generateReport('daily-log', { date });
  }, [generateReport]);

  return {
    fetchDailyLog,
    loading,
    error,
    data: reportData?.type === 'daily-log' ? reportData : null,
    fromCache
  };
};

export const useRevenueData = () => {
  const { generateReport, loading, error, reportData, fromCache } = useReportsData();

  const fetchRevenueReport = useCallback(async (startDate, endDate) => {
    return await generateReport('revenue', { startDate, endDate });
  }, [generateReport]);

  return {
    fetchRevenueReport,
    loading,
    error,
    data: reportData?.type === 'revenue' ? reportData : null,
    fromCache
  };
};

export const useEmployeePerformanceData = () => {
  const { generateReport, loading, error, reportData, fromCache } = useReportsData();

  const fetchEmployeePerformance = useCallback(async (startDate, endDate) => {
    return await generateReport('employee-performance', { startDate, endDate });
  }, [generateReport]);

  return {
    fetchEmployeePerformance,
    loading,
    error,
    data: reportData?.type === 'employee-performance' ? reportData : null,
    fromCache
  };
};

export const useCustomerActivityData = () => {
  const { generateReport, loading, error, reportData, fromCache } = useReportsData();

  const fetchCustomerActivity = useCallback(async (startDate, endDate) => {
    return await generateReport('customer-activity', { startDate, endDate });
  }, [generateReport]);

  return {
    fetchCustomerActivity,
    loading,
    error,
    data: reportData?.type === 'customer-activity' ? reportData : null,
    fromCache
  };
};

export const useCartUtilizationData = () => {
  const { generateReport, loading, error, reportData, fromCache } = useReportsData();

  const fetchCartUtilization = useCallback(async (startDate, endDate) => {
    return await generateReport('cart-utilization', { startDate, endDate });
  }, [generateReport]);

  return {
    fetchCartUtilization,
    loading,
    error,
    data: reportData?.type === 'cart-utilization' ? reportData : null,
    fromCache
  };
};